// SPDX-License-Identifier: MIT
/**
 * ember-lockup (closing, 5–7 beats; reviewed in the Kestrel reel). Embers stream in and assemble
 * the brand: the mark lands solid on the downbeat with a shockwave, the wordmark wipes out of it
 * while its tracking tightens, the tagline rises, and a specular sweep crosses the lockup.
 *
 * Adapts to the logo: a symbol gets the name set beside it; a logo that contains the name is used
 * alone; without a logo the embers assemble the typed name.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import type { Pt, Subpath } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { displayTracking, fitLine, logoHasWordmark, markBox, sceneBeats } from './lib.ts';
const CY = DESIGN_H / 2 - 40;
const GAP = 54;
export default defineScene({
    id: 'ember-lockup',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        /** Name set next to a symbol-only logo (default: the brand name). */
        wordmark: z.optional(z.string().check(z.maxLength(24))),
        tagline: z.string().check(z.maxLength(60)),
        url: z.optional(z.string().check(z.maxLength(40))),
    }),
    glyphs: (p) => (p.wordmark ?? '') + p.tagline + (p.url ?? ''),
    motion: (_p, env) => (env.localBeat < 2.6 ? 0.9 : 0.15),
    audioEvents: () => [{ beat: 3.2, sfx: 'blip' as const, gain: 0.5 }],
    post: (_p, env) => env.style.background === 'dark'
        ? {
            // Generous glow while embers fly; once the lockup lands it must be crisp.
            bloom: 1.2 - 0.75 * kit.beat.seg(env, 2.2, 3),
            flash: 0.22 * kit.beat.hits(env, [2], 10),
            shake: 0.8 * kit.beat.hits(env, [2], 9),
        }
        : { bloom: 0.4, flash: 0.12 * kit.beat.hits(env, [2], 10), shake: 0.6 * kit.beat.hits(env, [2], 9) },
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const brand = env.brand;
        const logo = brand.logo;
        const L = sceneBeats(env);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const mode = !logo ? 'text' : logoHasWordmark(brand) ? 'logo' : 'lockup';
        const word = mode === 'logo' ? '' : (props.wordmark ?? brand.name);
        // --- Geometry of the final lockup ------------------------------------------------------------
        let wordPx = 0;
        let ww = 0;
        let markB = { x: 0, y: 0, w: 0, h: 0 };
        if (mode === 'lockup') {
            wordPx = fitLine(ctx, brand, word, 700, 196, 1250);
            markB = markBox(brand, 0, 0, wordPx * 2.4, wordPx * 1.17);
        }
        else if (mode === 'text') {
            wordPx = fitLine(ctx, brand, word, 800, 220, 1500);
        }
        else {
            markB = markBox(brand, DESIGN_W / 2, CY, 1300, 300);
        }
        if (word) {
            ctx.font = font(brand, mode === 'text' ? 800 : 700, wordPx);
            ctx.letterSpacing = displayTracking(word, wordPx);
            ww = ctx.measureText(word).width;
            ctx.letterSpacing = '0px';
        }
        const total = mode === 'lockup' ? markB.w + GAP + ww : mode === 'text' ? ww : markB.w;
        const x0 = DESIGN_W / 2 - total / 2;
        if (mode === 'lockup')
            markB = { ...markB, x: x0, y: CY - markB.h / 2 };
        const wordX = mode === 'lockup' ? x0 + markB.w + GAP : x0;
        const markCx = mode === 'text' ? DESIGN_W / 2 : markB.x + markB.w / 2;
        const settle = kit.beat.seg(env, 2.4, L, kit.curves.silk);
        kit.fx.pool(ctx, DESIGN_W / 2, CY, 1000, th.primary, (th.dark ? 0.06 : 0.03) + 0.08 * kit.beat.seg(env, 1.6, 2.2));
        kit.camera.zoom(ctx, 1.06 - 0.06 * kit.beat.seg(env, 0, 2.2, kit.curves.glide) + 0.025 * settle);
        // --- Ember targets: the mark's fill, or the name's glyphs ---------------------------------------
        let mark: Subpath[] = [];
        let targets: Pt[];
        if (logo && mode !== 'text') {
            mark = kit.path.fit(logo.subpaths, markB);
            targets = kit.points.fromPaths(`lockup|${brand.name}|${mode}|${Math.round(markB.w)}`, mark, {
                step: 3,
                max: 1500,
                fillRule: logo.fillRule,
            });
            env.report?.element('logo', [markB.x, markB.y, markB.w, markB.h]);
        }
        else {
            targets = kit.points
                .fromText(word, font(brand, 800, wordPx), { step: 4, max: 1500, width: 1800, height: 400 })
                .map(([x, y]) => [x + DESIGN_W / 2, y + CY] as Pt);
            env.report?.element('logo', [x0, CY - wordPx / 2, ww, wordPx]);
        }
        const r = env.rng('embers');
        const land = kit.beat.seg(env, 2, 2.35);
        if (land < 1) {
            ctx.save();
            ctx.globalCompositeOperation = th.light;
            for (const [tx, ty] of targets) {
                const ang = r() * Math.PI * 2;
                const dist = 500 + r() * 900;
                const delay = r() * 0.9;
                const swirl = (r() - 0.5) * 2.2;
                const k = Math.min(1, kit.beat.seg(env, 0.1 + delay, 1.4 + delay, kit.curves.snap));
                const a = ang + swirl * (1 - k);
                const x = tx + Math.cos(a) * dist * (1 - k);
                const y = ty + Math.sin(a) * dist * (1 - k) * 0.6;
                const alpha = Math.min(1, (th.dark ? 0.55 : 0.8) + kit.beat.seg(env, delay * 0.5, delay * 0.5 + 0.4)) *
                    (1 - land);
                const size = th.dark ? 3.6 : 4.6;
                ctx.fillStyle = rgba(k > 0.97 ? th.hot : th.primary, alpha);
                ctx.fillRect(x - size / 2, y - size / 2, size, size);
            }
            ctx.restore();
        }
        // --- The brand lands solid on the downbeat ---------------------------------------------------------
        const solid = kit.beat.seg(env, 1.9, 2.05);
        if (solid > 0 && logo && mode !== 'text') {
            ctx.save();
            ctx.globalAlpha = solid;
            for (const part of logo.parts) {
                ctx.fillStyle = part.fill ?? th.primary;
                kit.path.tracePath(ctx, kit.path.fit(part.subpaths, markB, logo.bounds));
                ctx.fill(part.fillRule);
            }
            ctx.restore();
        }
        const wave = kit.beat.seg(env, 2, 3, kit.curves.glide);
        if (wave > 0 && wave < 1) {
            ctx.strokeStyle = rgba(th.dark ? th.hot : th.primary, 0.8 * (1 - wave));
            ctx.lineWidth = 6 * (1 - wave) + 1;
            ctx.beginPath();
            ctx.arc(markCx, CY, Math.max(markB.h, wordPx) * 0.6 + wave * 900, 0, Math.PI * 2);
            ctx.stroke();
        }
        // --- Name: wipes out of the mark (or lands with the embers), tracking tightening ----------------
        if (word) {
            const k = mode === 'text' ? solid : kit.beat.seg(env, 2.15, 3.0, kit.curves.glide);
            if (k > 0)
                env.crisp((c) => {
                    c.beginPath();
                    c.rect(wordX - 20, CY - wordPx, (ww + 80) * (mode === 'text' ? 1 : k), wordPx * 2);
                    c.clip();
                    c.font = font(brand, mode === 'text' ? 800 : 700, wordPx);
                    const track = parseFloat(displayTracking(word, wordPx));
                    c.letterSpacing = `${track + (mode === 'text' ? 0 : 26 * (1 - k))}px`;
                    c.fillStyle = mode === 'text' ? rgba(th.fg, k) : th.fg;
                    c.textBaseline = 'middle';
                    c.fillText(word, wordX - (mode === 'text' ? 0 : 40 * (1 - k)), CY + wordPx * 0.03);
                    c.letterSpacing = '0px';
                });
        }
        // --- Specular sweep across the lockup ----------------------------------------------------------------
        const sweep = kit.beat.seg(env, Math.min(4, L - 1.6), Math.min(5.2, L - 0.4), kit.curves.silk);
        kit.fx.sweep(ctx, (c) => {
            c.fillStyle = '#FFFFFF';
            if (mark.length) {
                kit.path.tracePath(c, mark);
                c.fill(logo?.fillRule ?? 'nonzero');
            }
            if (word) {
                c.font = font(brand, mode === 'text' ? 800 : 700, wordPx);
                c.letterSpacing = displayTracking(word, wordPx);
                c.textBaseline = 'middle';
                c.fillText(word, wordX, CY + wordPx * 0.03);
            }
        }, { x: x0, y: CY - Math.max(markB.h, wordPx) / 2 - 30, w: total, h: Math.max(markB.h, wordPx) + 60 }, sweep, { width: 140, intensity: th.dark ? 0.55 : 0.35 });
        // --- Tagline and url ----------------------------------------------------------------------------------
        const below = CY + Math.max(markB.h, wordPx) / 2 + 110;
        env.crisp((c) => {
            c.textAlign = 'center';
            c.font = font(brand, 500, 52);
            c.fillStyle = rgba(th.fg, 0.82);
            const lines = kit.text.wrap(c, props.tagline, 1400).slice(0, 2);
            kit.text.revealLines(c, lines, {
                x: DESIGN_W / 2,
                y: below,
                lineHeight: 66,
                align: 'center',
                progress: () => kit.beat.seg(env, 2.3, 2.9, kit.curves.glide),
            });
            if (props.url) {
                c.font = font(brand, 500, 26, 'mono');
                c.letterSpacing = '6px';
                c.fillStyle = rgba(th.accentText, kit.beat.seg(env, 2.9, 3.4));
                c.fillText(props.url, DESIGN_W / 2, below + 70 + (lines.length - 1) * 66);
                c.letterSpacing = '0px';
            }
        });
    },
});
